/**
 * L'éditeur de programme — le même pour l'organisateur (/orga) et pour le
 * compte personnel qui a créé son événement ; seule la base d'URL change.
 *
 * Modèle : l'Axis Business Festival. Une journée = des créneaux ; à chaque
 * créneau, une ou plusieurs activités (une par salle) : type, titre, entreprise
 * qui la porte, intervenants. La liste se lit comme le programme imprimé, et
 * chaque ligne s'ouvre pour être modifiée.
 */
import { useCallback, useEffect, useState } from 'react';
import { Clock, MapPin, Mic2, Plus, Trash2, Users, X } from 'lucide-react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Input, Modal, TextArea } from '../../admin/ui.tsx';
import { TYPES_SESSION, heure, libelleType, type Intervenant, type SessionProgramme, type TypeSession } from './types.ts';

const versLocal = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function ProgrammeEditeur({ base, jourParDefaut }: { base: string; jourParDefaut: string }) {
  const [sessions, setSessions] = useState<SessionProgramme[] | null>(null);
  const [edition, setEdition] = useState<SessionProgramme | 'nouvelle' | null>(null);

  const charger = useCallback(async () => {
    const r = await appelerApi<{ sessions: SessionProgramme[] }>(base);
    setSessions(r.donnees?.sessions ?? []);
  }, [base]);
  useEffect(() => {
    void charger();
  }, [charger]);

  const supprimer = async (s: SessionProgramme) => {
    await envoyer(`${base}/${s.id}`, {}, 'DELETE');
    await charger();
  };

  // Regroupé par heure de début, comme un programme imprimé.
  const creneaux = new Map<string, SessionProgramme[]>();
  for (const s of sessions ?? []) creneaux.set(s.debut_le, [...(creneaux.get(s.debut_le) ?? []), s]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-fumee">
          {sessions?.length ? `${sessions.length} activité${sessions.length > 1 ? 's' : ''}` : 'Aucune activité pour l’instant'}
        </p>
        <Btn variant="primary" onClick={() => setEdition('nouvelle')}>
          <Plus size={13} strokeWidth={1.5} className="mr-1.5 inline" />
          Ajouter une activité
        </Btn>
      </div>

      {sessions && sessions.length === 0 && (
        <p className="mt-3 max-w-[560px] text-[14px] leading-relaxed text-fumee">
          Accueil, mot de bienvenue, conférences par salle, speed meetings, tables rondes, pauses, keynote,
          afterwork : construisez la journée créneau par créneau. Les participants pourront dire « j’y assiste »
          et voir qui d’autre y sera.
        </p>
      )}

      <div className="mt-4 flex flex-col">
        {[...creneaux.entries()].map(([debut, liste]) => (
          <div key={debut} className="flex gap-4 border-b border-graphite py-3 last:border-0">
            <span className="w-14 shrink-0 pt-1 font-serif text-[18px] text-blanc">{heure(debut)}</span>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              {liste.map((s) => (
                <button key={s.id} onClick={() => setEdition(s)} className="group flex w-full items-start justify-between gap-3 rounded-[8px] px-2 py-1.5 text-left transition-colors hover:bg-[#262626]">
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2 text-[14px] text-blanc">
                      {s.salle && <span className="text-fumee">{s.salle} ·</span>}
                      {s.entreprise && <span className="text-fumee">{s.entreprise} —</span>}
                      {s.titre}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-fumee">
                      <Badge>{libelleType(s.type)}</Badge>
                      <span className="inline-flex items-center gap-1"><Clock size={11} strokeWidth={1.5} /> {heure(s.debut_le)} – {heure(s.fin_le)}</span>
                      {s.intervenants.length > 0 && (
                        <span className="inline-flex items-center gap-1"><Mic2 size={11} strokeWidth={1.5} /> {s.intervenants.map((i) => i.nom).join(', ')}</span>
                      )}
                      {s.participants > 0 && <span className="inline-flex items-center gap-1"><Users size={11} strokeWidth={1.5} /> {s.participants}</span>}
                    </span>
                  </span>
                  <span className="shrink-0 text-[12px] text-fumee opacity-0 transition-opacity group-hover:opacity-100">Modifier</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {edition && (
        <FormulaireSession
          base={base}
          existante={edition === 'nouvelle' ? null : edition}
          jourParDefaut={jourParDefaut}
          onClose={() => setEdition(null)}
          onFini={() => {
            setEdition(null);
            void charger();
          }}
          onSupprimer={edition === 'nouvelle' ? undefined : () => void supprimer(edition).then(() => setEdition(null))}
        />
      )}
    </div>
  );
}

function FormulaireSession({
  base,
  existante,
  jourParDefaut,
  onClose,
  onFini,
  onSupprimer,
}: {
  base: string;
  existante: SessionProgramme | null;
  jourParDefaut: string;
  onClose: () => void;
  onFini: () => void;
  onSupprimer?: () => void;
}) {
  const jour = jourParDefaut.slice(0, 10);
  const [type, setType] = useState<TypeSession>(existante?.type ?? 'conference');
  const [titre, setTitre] = useState(existante?.titre ?? '');
  const [debut, setDebut] = useState(existante ? versLocal(existante.debut_le) : `${jour}T10:00`);
  const [fin, setFin] = useState(existante ? versLocal(existante.fin_le) : `${jour}T10:30`);
  const [salle, setSalle] = useState(existante?.salle ?? '');
  const [entreprise, setEntreprise] = useState(existante?.entreprise ?? '');
  const [intervenants, setIntervenants] = useState<Intervenant[]>(existante?.intervenants ?? []);
  const [description, setDescription] = useState(existante?.description ?? '');
  const [lien, setLien] = useState(existante?.lien ?? '');
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const sansIntervenant = ['accueil', 'pause', 'lunch', 'networking', 'afterwork', 'speed_meeting'].includes(type);

  const enregistrer = async () => {
    setOccupe(true);
    setErreur(null);
    const corps = {
      titre: titre.trim(),
      type,
      debutLe: new Date(debut).toISOString(),
      finLe: new Date(fin).toISOString(),
      salle: salle.trim() || null,
      entreprise: entreprise.trim() || null,
      intervenants: intervenants.filter((i) => i.nom.trim()),
      description: description.trim() || null,
      lien: lien.trim() || null,
    };
    const r = existante ? await envoyer(`${base}/${existante.id}`, corps, 'PUT') : await envoyer(base, corps);
    setOccupe(false);
    if (r.erreur) return setErreur(r.erreur);
    onFini();
  };

  return (
    <Modal title={existante ? 'Modifier l’activité' : 'Nouvelle activité'} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div>
          <span className="label-caps block text-fumee">Type</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {TYPES_SESSION.map((t) => (
              <button key={t.cle} onClick={() => setType(t.cle)} className={`rounded-full border border-graphite px-3 py-1.5 text-[13px] transition-colors ${type === t.cle ? 'bg-blanc text-void' : 'text-fumee hover:text-blanc'}`}>
                {t.libelle}
              </button>
            ))}
          </div>
        </div>
        <Input label="Titre" value={titre} onChange={setTitre} placeholder={type === 'conference' ? 'Pourquoi investir dans l’immobilier neuf en Belgique' : type === 'speed_meeting' ? 'Speed Meeting #1' : 'Accueil'} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Début" value={debut} onChange={setDebut} type="datetime-local" />
          <Input label="Fin" value={fin} onChange={setFin} type="datetime-local" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Salle" value={salle} onChange={setSalle} placeholder="Salle A" />
          {!sansIntervenant && <Input label="Entreprise qui présente" value={entreprise} onChange={setEntreprise} placeholder="Immo Dussart" />}
        </div>

        {!sansIntervenant && (
          <div>
            <span className="label-caps block text-fumee">Intervenant{intervenants.length > 1 ? 's' : ''}</span>
            <div className="mt-2 flex flex-col gap-2">
              {intervenants.map((i, k) => (
                <div key={k} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <input value={i.nom} onChange={(e) => setIntervenants(intervenants.map((x, j) => (j === k ? { ...x, nom: e.target.value } : x)))} placeholder="Prénom Nom" className="field-input !min-h-[38px] !py-1.5 !text-sm" />
                  <input value={i.entreprise ?? ''} onChange={(e) => setIntervenants(intervenants.map((x, j) => (j === k ? { ...x, entreprise: e.target.value } : x)))} placeholder="Entreprise" className="field-input !min-h-[38px] !py-1.5 !text-sm" />
                  <button onClick={() => setIntervenants(intervenants.filter((_, j) => j !== k))} aria-label="Retirer" className="text-fumee hover:text-blanc"><X size={15} strokeWidth={1.5} /></button>
                </div>
              ))}
              {intervenants.length < 12 && (
                <button onClick={() => setIntervenants([...intervenants, { nom: '', entreprise: entreprise || '' }])} className="inline-flex items-center gap-1 self-start text-[13px] text-fumee hover:text-blanc">
                  <Plus size={13} strokeWidth={1.5} /> {type === 'table_ronde' ? 'Ajouter une voix à la table' : 'Ajouter un intervenant'}
                </button>
              )}
            </div>
          </div>
        )}

        <TextArea label="Description (facultative)" value={description} onChange={setDescription} rows={2} placeholder={type === 'speed_meeting' ? 'Rotations de 5 min.' : 'De quoi il sera question, pour qui.'} />
        <Input label="Lien (facultatif)" value={lien} onChange={setLien} placeholder="https://…" />
        {erreur && <p className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}

        <div className="flex items-center justify-between gap-3">
          <Btn variant="primary" onClick={() => void enregistrer()} disabled={occupe || titre.trim().length < 2}>
            {occupe ? '…' : existante ? 'Enregistrer' : 'Ajouter au programme'}
          </Btn>
          {onSupprimer && (
            <Btn onClick={onSupprimer}><Trash2 size={13} strokeWidth={1.5} className="mr-1 inline" />Supprimer</Btn>
          )}
        </div>
        <p className="text-[12px] text-fumee"><MapPin size={11} strokeWidth={1.5} className="mr-1 inline" />Deux activités au même créneau dans deux salles : ajoutez-les séparément avec leur salle.</p>
      </div>
    </Modal>
  );
}
